import React from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import debounce from 'lodash/debounce';
import Select, { createFilter } from 'react-select';
import i18n from '../../../i18n';
import styles from './AutoReplyCustomAwayMessageExpirationDropdown.module.css';
import { useRootStore } from '../../../../../mobx/bridge';
import { formatAutoReplyExpiration } from '../../../../../helpers/dateHelpers';

const stringify = (option) => option.label;
const filterOption = createFilter({ stringify });

const autoreplyExpirationTimePeriods = [
  {
    value: null,
    label: i18n.t('AutoReplyCustomAwayMessageExpirationDropdown.NoneExpirationTime'),
  },
];
const startDate = new Date();
const optionsCount = 48;
const minutesStep = 30;
startDate.setHours(0, 30, 0, 0);

for (let i = 0; i < optionsCount; i += 1) {
  let hours = startDate.getHours();
  const minutes = startDate.getMinutes();
  if (hours === 0 && minutes === 0) {
    hours = 24;
  }

  const formattedDate = [hours, String(minutes).padStart(2, '0')].join(':');
  const minutesPart = minutes === 30 ? '.5' : '';
  const ending =
    (hours === 0 && minutes === 30) || (hours === 1 && minutes === 0)
      ? i18n.t('AutoReplyCustomAwayMessageExpirationDropdown.Hour')
      : i18n.t('AutoReplyCustomAwayMessageExpirationDropdown.Hours');

  const label = `${hours}${minutesPart} ${ending}`;

  autoreplyExpirationTimePeriods.push({
    value: formattedDate,
    label,
  });
  startDate.setMinutes(startDate.getMinutes() + minutesStep);
}

const customAutoReplyDropdownStyles = {
  control: (provided) => ({
    ...provided,
    border: '1px solid #BEBEBE',
    borderRadius: '4px',
  }),
  option: (provided) => ({
    ...provided,
    font: 'normal normal normal 15px/20px Roboto, serif',
  }),
};

// eslint-disable-next-line prefer-arrow-callback,sonarjs/cognitive-complexity
const AutoReplyCustomAwayMessageExpirationDropdown = observer(function AutoReplyModalWindow(props) {
  const { data } = props;
  const { sendingEvents } = useRootStore();

  const [
    autoreplyExpirationSelectedOption,
    setAutoreplyExpirationSelectedOption,
  ] = React.useState(() =>
    data.SelectedExpirationTime
      ? autoreplyExpirationTimePeriods.find(
          (expiration) => expiration.value === data.SelectedExpirationTime
        )
      : autoreplyExpirationTimePeriods[0]
  );

  React.useEffect(() => {
    const newExpirationDate = data.SelectedExpirationTime
      ? autoreplyExpirationTimePeriods.find(
          (expiration) => expiration.value === data.SelectedExpirationTime
        )
      : autoreplyExpirationTimePeriods[0];

    setAutoreplyExpirationSelectedOption(newExpirationDate);
  }, [data?.SelectedExpirationTime]);

  const { t } = useTranslation();

  const handleChangeAutoReplyExpirationPeriod = debounce((option) => {
    setAutoreplyExpirationSelectedOption(option);

    sendingEvents.socketEmitSetCustomAutoRepliesExpiration(data._id, option.value);
  }, 250);

  let expiration = t('Common.N/A');

  if (autoreplyExpirationSelectedOption?.value && data?.ExpirationTime) {
    expiration = formatAutoReplyExpiration(data.ExpirationTime);
  }

  return (
    <div>
      <div className={styles.expirationDateTextContainer}>
        <span className={styles.expirationDateText}>
          {t('Common.AutoReplyMessageExpiration', { expiration })}
        </span>
      </div>
      <Select
        styles={customAutoReplyDropdownStyles}
        className={styles.timeSelectDropdown}
        filterOption={filterOption}
        value={autoreplyExpirationSelectedOption}
        onChange={handleChangeAutoReplyExpirationPeriod}
        options={autoreplyExpirationTimePeriods}
        defaultValue={autoreplyExpirationTimePeriods[0]}
        menuPortalTarget={document.body}
        menuPosition="fixed"
      />
    </div>
  );
});
AutoReplyCustomAwayMessageExpirationDropdown.whyDidYouRender = true;
AutoReplyCustomAwayMessageExpirationDropdown.displayName =
  'AutoReplyCustomAwayMessageExpirationDropdown';

export default AutoReplyCustomAwayMessageExpirationDropdown;
